// ============================================================
// Ebek Sales CRM — Bulk WhatsApp Messaging
// Filter leads → select → compose → send blast → report
// ============================================================
import React, { useEffect, useState, useCallback } from "react";
import toast from "react-hot-toast";
import API from "../../utils/api";

const STAGE_OPTS = [
  { key: "",                  label: "All Stages" },
  { key: "visited",           label: "Visited" },
  { key: "interested",        label: "Interested" },
  { key: "proposal_sent",     label: "Proposal Sent" },
  { key: "mou_shared",        label: "MOU Shared" },
  { key: "mou_signed",        label: "MOU Signed" },
  { key: "payment_requested", label: "Payment Requested" },
];

const TEMP_OPTS = [
  { key: "",     label: "All Temps" },
  { key: "hot",  label: "🔥 Hot" },
  { key: "warm", label: "🌤 Warm" },
  { key: "cold", label: "❄️ Cold" },
];

const TEMPLATES = [
  {
    label: "Introduction",
    text: "Hello {name}! I'm from Ebek Education. We help students improve their academic performance through personalized learning tools. I'd love to share more about how we can support {institution}. Would you have a few minutes this week?",
  },
  {
    label: "Follow-up",
    text: "Hi {name}, following up on our recent conversation about Ebek's learning solutions for {institution}. Please let me know if you have any questions — I'm happy to arrange a demo or share more details.",
  },
  {
    label: "Proposal Reminder",
    text: "Dear {name}, hope you're doing well! I wanted to check if you had a chance to review the proposal I shared for {institution}. Please feel free to reach out for any clarification. Looking forward to hearing from you.",
  },
  {
    label: "MOU Follow-up",
    text: "Hi {name}, just following up on the MOU we shared with {institution}. Could you let us know if there are any questions or changes needed before signing? We're excited to move forward together!",
  },
  {
    label: "Payment Reminder",
    text: "Dear {name}, this is a gentle reminder regarding the payment request we raised for {institution}. Kindly let us know if you need any clarification or if there's anything we can assist with to complete the process.",
  },
];

function personalise(template, lead) {
  return template
    .replace(/{name}/g,        lead.contact_name     || lead.institution_name)
    .replace(/{institution}/g, lead.institution_name || "your institution");
}

const STAGE_COLOR = {
  visited:           { color: "#64748b", bg: "#f1f5f9" },
  interested:        { color: "#1d4ed8", bg: "#dbeafe" },
  proposal_sent:     { color: "#c2410c", bg: "#ffedd5" },
  mou_shared:        { color: "#7c3aed", bg: "#ede9fe" },
  mou_signed:        { color: "#4f46e5", bg: "#e0e7ff" },
  payment_requested: { color: "#b45309", bg: "#fef3c7" },
};

export default function BulkMessage({ accent }) {
  const [leads, setLeads]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [filterStage, setStage]   = useState("");
  const [filterTemp,  setTemp]    = useState("");
  const [selected, setSelected]   = useState(new Set());
  const [message, setMessage]     = useState("");
  const [usePersonalise, setPersonalise] = useState(true);
  const [sending, setSending]     = useState(false);
  const [report, setReport]       = useState(null); // null | { summary, results }
  const [step, setStep]           = useState("select"); // "select" | "compose" | "report"

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterStage) params.pipeline_stage = filterStage;
      if (filterTemp)  params.temperature     = filterTemp;
      const r = await API.get("/leads/mine", { params });
      setLeads(r.data.filter(l => l.is_active && l.pipeline_stage !== "closed_lost" && l.pipeline_stage !== "closed_won"));
    } catch {
      toast.error("Failed to load leads");
    } finally {
      setLoading(false);
    }
  }, [filterStage, filterTemp]);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  // Keep selection in sync with filter changes
  useEffect(() => { setSelected(new Set()); }, [filterStage, filterTemp]);

  function toggleLead(id) {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  function toggleAll() {
    const withPhone = leads.filter(l => l.contact_phone);
    if (selected.size === withPhone.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(withPhone.map(l => l.id)));
    }
  }

  async function handleSend() {
    if (selected.size === 0) return toast.error("Select at least one lead");
    if (!message.trim()) return toast.error("Type a message to send");
    setSending(true);
    try {
      const selectedLeads = leads.filter(l => selected.has(l.id));
      // If personalise is on, we send per-lead; otherwise use one message for all
      let results = [];
      let summary = { sent: 0, failed: 0, skipped: 0, total: selected.size };

      if (usePersonalise) {
        // Send individually with personalised text
        for (const lead of selectedLeads) {
          if (!lead.contact_phone) {
            results.push({ lead_id: lead.id, institution_name: lead.institution_name, status: "skipped", reason: "No phone" });
            summary.skipped++;
            continue;
          }
          try {
            const personalMsg = personalise(message, lead);
            await API.post("/whatsapp/send", {
              lead_id: lead.id,
              phone:   lead.contact_phone,
              message: personalMsg,
            });
            results.push({ lead_id: lead.id, institution_name: lead.institution_name, contact_name: lead.contact_name, phone: lead.contact_phone, status: "sent", message: personalMsg });
            summary.sent++;
          } catch (err) {
            results.push({ lead_id: lead.id, institution_name: lead.institution_name, contact_name: lead.contact_name, phone: lead.contact_phone, status: "failed", reason: err?.response?.data?.error || err.message });
            summary.failed++;
          }
          await new Promise(r => setTimeout(r, 150));
        }
      } else {
        // Bulk send (backend handles loop)
        const r = await API.post("/whatsapp/bulk-send", {
          lead_ids: Array.from(selected),
          message:  message.trim(),
        });
        results  = r.data.results;
        summary  = r.data.summary;
      }

      setReport({ summary, results });
      setStep("report");
      toast.success(`Sent to ${summary.sent} lead${summary.sent !== 1 ? "s" : ""} ✓`);
    } catch (err) {
      toast.error(err?.response?.data?.error || "Bulk send failed");
    } finally {
      setSending(false);
    }
  }

  function applyTemplate(tmpl) {
    setMessage(tmpl.text);
  }

  const withPhone    = leads.filter(l => l.contact_phone);
  const allSelected  = withPhone.length > 0 && selected.size === withPhone.length;
  const selectedList = leads.filter(l => selected.has(l.id));

  // ── REPORT VIEW ───────────────────────────────────────────
  if (step === "report" && report) {
    const { summary, results } = report;
    return (
      <div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <div>
            <div style={{ fontSize: 20, fontWeight: 800, color: "#1e293b" }}>💬 Delivery Report</div>
            <div style={{ fontSize: 13, color: "#64748b" }}>Bulk WhatsApp send results</div>
          </div>
          <button onClick={() => { setStep("select"); setReport(null); setSelected(new Set()); }}
            style={{ padding: "8px 16px", borderRadius: 10, border: "none", background: accent, color: "#fff", fontFamily: "inherit", fontWeight: 700, fontSize: 12, cursor: "pointer" }}>
            New Blast
          </button>
        </div>

        {/* Summary tiles */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 10, marginBottom: 20 }}>
          {[
            { label: "Sent",    count: summary.sent,    color: "#15803d", bg: "#dcfce7", icon: "✅" },
            { label: "Failed",  count: summary.failed,  color: "#dc2626", bg: "#fee2e2", icon: "❌" },
            { label: "Skipped", count: summary.skipped, color: "#92400e", bg: "#fef3c7", icon: "⚠️" },
          ].map(t => (
            <div key={t.label} style={{ background: t.bg, borderRadius: 12, padding: "12px 10px", textAlign: "center" }}>
              <div style={{ fontSize: 24 }}>{t.icon}</div>
              <div style={{ fontSize: 20, fontWeight: 800, color: t.color }}>{t.count}</div>
              <div style={{ fontSize: 11, fontWeight: 700, color: t.color }}>{t.label}</div>
            </div>
          ))}
        </div>

        {/* Per-lead results */}
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {results.map((r, i) => (
            <div key={i} style={{ background: "#fff", border: `1.5px solid ${r.status === "sent" ? "#dcfce7" : r.status === "failed" ? "#fee2e2" : "#fef9c3"}`, borderRadius: 12, padding: "12px 14px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#1e293b" }}>{r.institution_name}</div>
                {r.contact_name && <div style={{ fontSize: 11, color: "#64748b" }}>{r.contact_name}</div>}
                {r.phone && <div style={{ fontSize: 11, color: "#94a3b8" }}>{r.phone}</div>}
                {r.reason && <div style={{ fontSize: 11, color: "#dc2626", marginTop: 2 }}>⚠️ {r.reason}</div>}
              </div>
              <span style={{
                padding: "4px 10px", borderRadius: 100, fontSize: 11, fontWeight: 800, flexShrink: 0,
                background: r.status === "sent" ? "#dcfce7" : r.status === "failed" ? "#fee2e2" : "#fef9c3",
                color:      r.status === "sent" ? "#15803d" : r.status === "failed" ? "#dc2626"  : "#92400e",
              }}>
                {r.status === "sent" ? "✅ Sent" : r.status === "failed" ? "❌ Failed" : "⚠️ Skipped"}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ── COMPOSE VIEW ──────────────────────────────────────────
  if (step === "compose") {
    return (
      <div>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
          <button onClick={() => setStep("select")}
            style={{ background: "#f1f5f9", border: "none", borderRadius: 8, padding: "6px 12px", cursor: "pointer", fontFamily: "inherit", fontWeight: 700, fontSize: 12, color: "#64748b" }}>
            ← Back
          </button>
          <div>
            <div style={{ fontSize: 18, fontWeight: 800, color: "#1e293b" }}>💬 Compose Message</div>
            <div style={{ fontSize: 12, color: "#64748b" }}>{selected.size} lead{selected.size !== 1 ? "s" : ""} selected</div>
          </div>
        </div>

        {/* Templates */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 800, color: "#64748b", textTransform: "uppercase", marginBottom: 8 }}>Quick Templates</div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {TEMPLATES.map(t => (
              <button key={t.label} onClick={() => applyTemplate(t)}
                style={{ padding: "7px 12px", borderRadius: 8, border: "1.5px solid #e2e8f0", background: "#fff", cursor: "pointer", fontFamily: "inherit", fontWeight: 700, fontSize: 11, color: "#475569" }}>
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {/* Message composer */}
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 11, fontWeight: 800, color: "#64748b", textTransform: "uppercase", marginBottom: 8 }}>
            Message
            <span style={{ marginLeft: 8, fontSize: 10, fontWeight: 500, color: "#94a3b8" }}>
              Use {"{name}"} and {"{institution}"} for personalisation
            </span>
          </div>
          <textarea value={message} onChange={e => setMessage(e.target.value)}
            placeholder="Type your message here…"
            rows={6}
            style={{ width: "100%", padding: "12px 14px", border: "1.5px solid #e2e8f0", borderRadius: 12, fontSize: 13, fontFamily: "inherit", outline: "none", resize: "vertical", boxSizing: "border-box", lineHeight: 1.6 }}
          />
          <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 4, textAlign: "right" }}>{message.length} chars</div>
        </div>

        {/* Personalise toggle */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20, padding: "10px 14px", background: "#f0fdf4", borderRadius: 10, border: "1px solid #dcfce7" }}>
          <button onClick={() => setPersonalise(v => !v)}
            style={{ width: 36, height: 20, borderRadius: 100, border: "none", cursor: "pointer", padding: 2, background: usePersonalise ? "#15803d" : "#cbd5e1", transition: "background 0.2s", display: "flex", alignItems: "center" }}>
            <div style={{ width: 16, height: 16, borderRadius: "50%", background: "#fff", transform: usePersonalise ? "translateX(16px)" : "translateX(0)", transition: "transform 0.2s" }} />
          </button>
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#15803d" }}>Personalise messages</div>
            <div style={{ fontSize: 11, color: "#64748b" }}>{usePersonalise ? `Each lead gets a personalised message with their name & institution` : "Same message sent to everyone"}</div>
          </div>
        </div>

        {/* Preview for first selected lead */}
        {usePersonalise && selectedList[0] && message.includes("{") && (
          <div style={{ marginBottom: 20, background: "#f8fafc", border: "1.5px solid #e2e8f0", borderRadius: 12, padding: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 800, color: "#64748b", textTransform: "uppercase", marginBottom: 8 }}>
              Preview — {selectedList[0].institution_name}
            </div>
            <div style={{ fontSize: 13, color: "#1e293b", lineHeight: 1.6, whiteSpace: "pre-wrap" }}>
              {personalise(message, selectedList[0])}
            </div>
          </div>
        )}

        {/* Send button */}
        <button onClick={handleSend} disabled={sending || !message.trim()}
          style={{ width: "100%", padding: "15px", background: message.trim() && !sending ? "#15803d" : "#e2e8f0",
            color: message.trim() && !sending ? "#fff" : "#94a3b8",
            border: "none", borderRadius: 14, fontSize: 15, fontWeight: 800, cursor: message.trim() && !sending ? "pointer" : "not-allowed", fontFamily: "inherit" }}>
          {sending ? `Sending to ${selected.size} leads…` : `💬 Send to ${selected.size} Lead${selected.size !== 1 ? "s" : ""}`}
        </button>
        <div style={{ fontSize: 11, color: "#94a3b8", textAlign: "center", marginTop: 8 }}>
          Only leads with a saved phone number will receive the message
        </div>
      </div>
    );
  }

  // ── SELECT VIEW ───────────────────────────────────────────
  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 20, fontWeight: 800, color: "#1e293b" }}>💬 Bulk WhatsApp</div>
        <div style={{ fontSize: 13, color: "#64748b" }}>Select leads and send a message blast</div>
      </div>

      {/* Filters */}
      <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap" }}>
        <select value={filterStage} onChange={e => setStage(e.target.value)}
          style={{ padding: "8px 12px", borderRadius: 10, border: "1.5px solid #e2e8f0", fontFamily: "inherit", fontSize: 12, fontWeight: 700, color: "#475569", background: "#fff", outline: "none" }}>
          {STAGE_OPTS.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
        </select>
        <select value={filterTemp} onChange={e => setTemp(e.target.value)}
          style={{ padding: "8px 12px", borderRadius: 10, border: "1.5px solid #e2e8f0", fontFamily: "inherit", fontSize: 12, fontWeight: 700, color: "#475569", background: "#fff", outline: "none" }}>
          {TEMP_OPTS.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
        </select>
      </div>

      {/* Select bar */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <button onClick={toggleAll}
            style={{ padding: "7px 12px", borderRadius: 8, border: `2px solid ${allSelected ? accent : "#e2e8f0"}`, background: allSelected ? accent : "#fff", color: allSelected ? "#fff" : "#64748b", fontFamily: "inherit", fontWeight: 700, fontSize: 11, cursor: "pointer" }}>
            {allSelected ? "✓ All Selected" : "Select All"}
          </button>
          {selected.size > 0 && (
            <span style={{ fontSize: 12, color: "#64748b", fontWeight: 700 }}>
              {selected.size} selected · {leads.filter(l => selected.has(l.id) && !l.contact_phone).length} no phone
            </span>
          )}
        </div>
        {selected.size > 0 && (
          <button onClick={() => setStep("compose")}
            style={{ padding: "9px 18px", borderRadius: 10, border: "none", background: "#15803d", color: "#fff", fontFamily: "inherit", fontWeight: 800, fontSize: 13, cursor: "pointer" }}>
            Next: Compose →
          </button>
        )}
      </div>

      {/* Lead list */}
      {loading ? (
        <div style={{ textAlign: "center", color: "#94a3b8", padding: 40 }}>Loading…</div>
      ) : leads.length === 0 ? (
        <div style={{ textAlign: "center", padding: 40, color: "#94a3b8" }}>
          <div style={{ fontSize: 36, marginBottom: 10 }}>📋</div>
          <div style={{ fontWeight: 600 }}>No leads found</div>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {leads.map(l => {
            const stageColor  = STAGE_COLOR[l.pipeline_stage] || { color: "#64748b", bg: "#f1f5f9" };
            const isSelected  = selected.has(l.id);
            const hasPhone    = !!l.contact_phone;
            return (
              <div key={l.id} onClick={() => hasPhone && toggleLead(l.id)}
                style={{ background: "#fff", border: `2px solid ${isSelected ? accent : "#e2e8f0"}`,
                  borderRadius: 12, padding: "12px 14px", cursor: hasPhone ? "pointer" : "default",
                  opacity: hasPhone ? 1 : 0.55, display: "flex", alignItems: "center", gap: 12,
                  transition: "border-color 0.15s" }}>
                {/* Checkbox */}
                <div style={{ width: 22, height: 22, borderRadius: 6, border: `2px solid ${isSelected ? accent : "#cbd5e1"}`,
                  background: isSelected ? accent : "#fff", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, color: "#fff" }}>
                  {isSelected && "✓"}
                </div>
                {/* Lead info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#1e293b" }}>{l.institution_name}</div>
                  <div style={{ fontSize: 11, color: "#64748b" }}>
                    {l.contact_name ? `${l.contact_name} · ` : ""}
                    {hasPhone ? l.contact_phone : <span style={{ color: "#ef4444" }}>No phone number</span>}
                  </div>
                </div>
                {/* Stage pill */}
                <span style={{ background: stageColor.bg, color: stageColor.color, borderRadius: 100, padding: "3px 9px", fontSize: 10, fontWeight: 700, flexShrink: 0 }}>
                  {l.pipeline_stage?.replace(/_/g, " ")}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
